/**
 * Report presentation (UI phase D, ADR 0031): number formatting, summary figures, chart series, the
 * previous equal period and data-coverage notes. Pure TypeScript (no DOM, no React), so the same figures
 * feed the screen, the PDF (built in a Web Worker) and the tests.
 *
 * Every number here is derived from rows the existing report RPCs already returned. Nothing is estimated
 * or filled in: a value the rows do not carry is null and is shown as "—".
 */
import {
  DEVICE_HEALTH_LIMITS,
  drivingBandShares,
  zonedTimeToUtc,
  type ChecklistComplianceRow,
  type DrivingResult,
  type EventRow,
  type UsageRow,
  type Device,
  type Vehicle,
} from "@fts/core";

/* ---------------- numbers ---------------- */

/** Locale number with grouping: "1.234,5" (tr-TR), "1,234.5" (en-GB). Always `digits` decimals. */
export function formatNum(v: number, locale: string, digits = 1): string {
  return new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v);
}

/** Whole numbers with grouping ("12.480" / "12,480"). */
export function formatInt(v: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(v));
}

/** Percent with the sign where the language puts it: "%35,6" (TR), "35.6%" (EN). Always `digits` decimals. */
export function formatPct(v: number, locale: string, digits = 1): string {
  return new Intl.NumberFormat(locale, { style: "percent", minimumFractionDigits: digits, maximumFractionDigits: digits }).format(v / 100);
}

/** Seconds → hours (one decimal), for chart axes and summary figures in hours. */
export const toHours = (seconds: number) => seconds / 3600;

/* ---------------- periods ---------------- */

const DAY = 86_400_000;
const dayMs = (d: string) => Date.parse(`${d}T12:00:00Z`);
export const shiftDay = (d: string, n: number) => new Date(dayMs(d) + n * DAY).toISOString().slice(0, 10);

/** Inclusive number of local days. */
export function periodDays(from: string, to: string): number {
  return Math.round((dayMs(to) - dayMs(from)) / DAY) + 1;
}

/** The equal period right before [from, to] (same number of days). */
export function previousPeriod(from: string, to: string): { from: string; to: string } {
  const n = periodDays(from, to);
  return { from: shiftDay(from, -n), to: shiftDay(from, -1) };
}

/** Every local day of the period, in order. */
export function daysOf(from: string, to: string): string[] {
  const n = periodDays(from, to);
  return Array.from({ length: Math.max(0, n) }, (_, i) => shiftDay(from, i));
}

/* ---------------- comparison with the previous period ---------------- */

export type Trend =
  | { kind: "flat" }
  | { kind: "new" } // previous 0, now > 0: no percentage
  | { kind: "up" | "down"; delta: number; unit: "pct" | "pts" };

/**
 * Change from the previous period. `unit: "pct"` = relative change in % (counts, hours, km); `"pts"` =
 * difference in percentage points (rates that are already percentages). null = no comparison possible.
 */
export function trendOf(current: number | null, previous: number | null, unit: "pct" | "pts" = "pct"): Trend | null {
  if (current === null || previous === null || !Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (unit === "pts") {
    const d = Math.round((current - previous) * 10) / 10;
    return d === 0 ? { kind: "flat" } : { kind: d > 0 ? "up" : "down", delta: Math.abs(d), unit };
  }
  if (previous === 0) return current === 0 ? { kind: "flat" } : { kind: "new" };
  const d = Math.round((1000 * (current - previous)) / previous) / 10;
  return d === 0 ? { kind: "flat" } : { kind: d > 0 ? "up" : "down", delta: Math.abs(d), unit };
}

/* ---------------- usage ---------------- */

export type UsageTotals = {
  ignitionS: number;
  movingS: number;
  idleS: number;
  distanceKm: number;
  /** Ignition time inside the basis ÷ basis time (%); null without basis time. */
  utilisation: number | null;
  vehicles: number;
  /** Basis time with the ignition not on (off, or no data), seconds. */
  offInBasisS: number;
};

/** Totals of the usage table (the same sums the table's rows add up to). */
export function usageTotals(rows: readonly UsageRow[]): UsageTotals {
  let ign = 0, mov = 0, idle = 0, dist = 0, basis = 0, bi = 0;
  for (const r of rows) {
    ign += r.ignitionS;
    mov += r.movingS;
    idle += r.idleS;
    dist += r.distanceM;
    basis += r.basisS;
    bi += r.basisIgnitionS;
  }
  return {
    ignitionS: ign,
    movingS: mov,
    idleS: idle,
    distanceKm: dist / 1000,
    utilisation: basis > 0 ? (100 * bi) / basis : null,
    vehicles: new Set(rows.map((r) => r.vehicleId)).size,
    offInBasisS: Math.max(0, basis - bi),
  };
}

/** One bar of a stacked chart: a category and one value per series (same order as the series). */
export type ChartBar = { key: string; label: string; values: number[] };

/**
 * Usage chart, per day: moving, idle and "ignition not on / no data within the basis" hours. The last one
 * is the basis time (shifts / working hours / 24 h, as chosen) minus the ignition time inside it — the
 * same numbers as the utilisation column; time without data counts there too (the device cannot tell the
 * two apart, so the series says both). The three parts cover different windows (the first two the whole
 * day, the third the basis only): the chart shows no stacked total. Days without any row get an empty bar.
 */
export function usageByDay(rows: readonly UsageRow[], from: string, to: string): ChartBar[] {
  const by = new Map<string, number[]>(daysOf(from, to).map((d) => [d, [0, 0, 0]]));
  for (const r of rows) {
    const v = by.get(r.day);
    if (!v) continue;
    v[0]! += r.movingS;
    v[1]! += r.idleS;
    v[2]! += Math.max(0, r.basisS - r.basisIgnitionS);
  }
  return [...by].map(([day, v]) => ({ key: day, label: day, values: v.map(toHours) }));
}

/** Usage chart, per vehicle (fleet number order), same three series. */
export function usageByVehicle(rows: readonly UsageRow[]): ChartBar[] {
  const by = new Map<string, { label: string; v: number[] }>();
  for (const r of rows) {
    const e = by.get(r.vehicleId) ?? { label: r.fleetNo, v: [0, 0, 0] };
    e.v[0]! += r.movingS;
    e.v[1]! += r.idleS;
    e.v[2]! += Math.max(0, r.basisS - r.basisIgnitionS);
    by.set(r.vehicleId, e);
  }
  return [...by]
    .sort((a, b) => a[1].label.localeCompare(b[1].label, "tr", { numeric: true }))
    .map(([id, e]) => ({ key: id, label: e.label, values: e.v.map(toHours) }));
}

/* ---------------- events ---------------- */

export type EventTotals = { total: number; impacts: number; severe: number; open: number; offHours: number };

export function eventTotals(rows: readonly EventRow[]): EventTotals {
  let impacts = 0, severe = 0, open = 0, off = 0;
  for (const r of rows) {
    if (r.type === "impact") {
      impacts++;
      if (r.severity >= 3) severe++;
    }
    if (r.status === "open") open++;
    if (r.offHours) off++;
  }
  return { total: rows.length, impacts, severe, open, offHours: off };
}

/** Local day (YYYY-MM-DD) of an instant in a time zone. */
export function localDay(iso: string, tz: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(iso));
  const g = (k: string) => parts.find((p) => p.type === k)?.value ?? "";
  return `${g("year")}-${g("month")}-${g("day")}`;
}

/**
 * Alarms per local day, stacked: impacts at level 1, 2, 3 and every other alarm type (whose severity is a
 * fixed 2 and says nothing about how hard). One alarm row counts once (folded repeats are one alarm, as in
 * the table).
 */
export function eventsByDay(rows: readonly EventRow[], from: string, to: string, tz: string): ChartBar[] {
  const by = new Map<string, number[]>(daysOf(from, to).map((d) => [d, [0, 0, 0, 0]]));
  for (const r of rows) {
    const v = by.get(localDay(r.occurredAt, tz));
    if (!v) continue;
    if (r.type === "impact") v[Math.max(1, Math.min(3, r.severity)) - 1]!++;
    else v[3]!++;
  }
  return [...by].map(([day, v]) => ({ key: day, label: day, values: v }));
}

/* ---------------- checklist ---------------- */

export type ChecklistTotals = {
  shiftsUsed: number;
  shiftsChecked: number;
  missed: number;
  compliance: number | null;
  faultsOpened: number;
  faultsResolved: number;
  /** Mean hours to resolve, weighted by the faults resolved per row; null without resolved faults. */
  mttrH: number | null;
};

export function checklistTotals(rows: readonly ChecklistComplianceRow[]): ChecklistTotals {
  let used = 0, checked = 0, missed = 0, opened = 0, resolved = 0, mttrW = 0, mttrN = 0;
  for (const r of rows) {
    used += r.shiftsUsed ?? 0;
    checked += r.shiftsChecked ?? 0;
    missed += r.missed ?? 0;
    opened += r.faultsOpened;
    resolved += r.faultsResolved;
    if (r.mttrH !== null && r.faultsResolved > 0) {
      mttrW += r.mttrH * r.faultsResolved;
      mttrN += r.faultsResolved;
    }
  }
  return {
    shiftsUsed: used,
    shiftsChecked: checked,
    missed,
    compliance: used > 0 ? (100 * checked) / used : null,
    faultsOpened: opened,
    faultsResolved: resolved,
    mttrH: mttrN > 0 ? mttrW / mttrN : null,
  };
}

/** Compliance per vehicle (%), lowest first; vehicles without shifts or without used shifts are left out. */
export function complianceByVehicle(rows: readonly ChecklistComplianceRow[]): ChartBar[] {
  const by = new Map<string, { label: string; used: number; checked: number }>();
  for (const r of rows) {
    if (!r.hasShifts || r.shiftsUsed === null) continue;
    const e = by.get(r.vehicleId) ?? { label: r.fleetNo, used: 0, checked: 0 };
    e.used += r.shiftsUsed;
    e.checked += r.shiftsChecked ?? 0;
    by.set(r.vehicleId, e);
  }
  return [...by]
    .filter(([, e]) => e.used > 0)
    .map(([id, e]) => ({ key: id, label: e.label, values: [(100 * e.checked) / e.used] }))
    .sort((a, b) => a.values[0]! - b.values[0]! || a.label.localeCompare(b.label, "tr", { numeric: true }));
}

/* ---------------- driving ---------------- */

/**
 * Share of moving time per speed band (%), the totals first and then every row with moving time. Suppressed
 * rows (KVKK) and rows without moving time are left out. Shares come from drivingBandShares, the same
 * numbers as the table's band columns.
 */
export function drivingBandBars(res: Pick<DrivingResult, "rows" | "totals">, totalsLabel: string, rowLabel: (r: DrivingResult["rows"][number]) => string): ChartBar[] {
  const bar = (key: string, label: string, bandS: readonly number[]): ChartBar | null => {
    const s = drivingBandShares(bandS);
    return s.every((x) => x === null) ? null : { key, label, values: s.map((x) => x ?? 0) };
  };
  const out: ChartBar[] = [];
  const total = bar("totals", totalsLabel, res.totals.bandS);
  if (total) out.push(total);
  for (const r of res.rows) {
    if (r.suppressed) continue;
    const b = bar(`${r.kind}:${r.id ?? ""}`, rowLabel(r), r.bandS);
    if (b) out.push(b);
  }
  return out;
}

/* ---------------- coverage ---------------- */

/**
 * A device whose last contact is older than this is listed with its last data time: the device-health
 * "not current" threshold (30 min, DEVICE_HEALTH_LIMITS.staleAfterS). A quiet device is not called
 * offline — a vehicle parked for the evening sends nothing either — the note only says when the last data
 * arrived, which the console knows for certain.
 */
export const SILENT_AFTER_MS = DEVICE_HEALTH_LIMITS.staleAfterS * 1000;

export type Coverage = {
  /** Days of the period after today (no data can exist yet). */
  futureDays: number;
  /** Vehicles whose device last reported inside the period, more than SILENT_AFTER_MS before its end (or now). */
  lastDataAt: { fleetNo: string; at: string }[];
  /** Vehicles whose device last reported before the period began (or never). */
  silentAll: string[];
};

/**
 * What the console already knows about gaps: days not yet reached, and the last contact of each device
 * (devices.last_seen_at). It cannot see a device that was offline in the middle of the period and came
 * back, nor tell a parked vehicle from an offline device; the note says only what is known.
 */
export function coverageOf(opts: {
  vehicles: readonly Vehicle[];
  devices: readonly Device[];
  from: string;
  to: string;
  tz: string;
  today: string;
  now?: number;
}): Coverage {
  const now = opts.now ?? Date.now();
  const firstFuture = opts.today >= opts.from ? shiftDay(opts.today, 1) : opts.from;
  const futureDays = opts.to >= firstFuture ? periodDays(firstFuture, opts.to) : 0;
  const start = zonedTimeToUtc(opts.from, "00:00", opts.tz).getTime();
  const end = Math.min(now, zonedTimeToUtc(shiftDay(opts.to, 1), "00:00", opts.tz).getTime());
  const lastDataAt: Coverage["lastDataAt"] = [];
  const silentAll: string[] = [];
  // A period that has not begun yet: nothing could have been sent, nothing to say about devices.
  if (start >= now) return { futureDays, lastDataAt, silentAll };
  for (const v of [...opts.vehicles].sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true }))) {
    const devs = opts.devices.filter((d) => d.vehicleId === v.id && !d.revoked);
    if (devs.length === 0) continue; // no device: nothing is expected
    const seen = devs.map((d) => (d.lastSeenAt ? Date.parse(d.lastSeenAt) : NaN)).filter((x) => Number.isFinite(x));
    const last = seen.length ? Math.max(...seen) : null;
    if (last === null || last < start) silentAll.push(v.fleetNo);
    else if (last < end && end - last > SILENT_AFTER_MS) lastDataAt.push({ fleetNo: v.fleetNo, at: new Date(last).toISOString() });
  }
  return { futureDays, lastDataAt, silentAll };
}

export const hasCoverageIssue = (c: Coverage) => c.futureDays > 0 || c.lastDataAt.length > 0 || c.silentAll.length > 0;
